import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MY_STATE_LABELS } from "@gaole/shared";
import { FLEXXI_KYC_STATUS_LABELS } from "@gaole/shared";
import { api, ApiError } from "../../lib/api";
import { toast } from "../../stores/toast";
import { Button, PageLoader } from "../../components/ui";
import { PageHeader } from "../../components/storefront/PageHeader";
import { PrivateKycUploadField } from "../../components/flexxi/kyc/PrivateKycUploadField";

const ID_TYPES = ["MyKad / NRIC", "Passport"] as const;
const STEPS = ["Identity", "Address", "Selfie"] as const;
type Step = (typeof STEPS)[number];
const IC_MAX = 12;

type KycState = {
  legalName: string;
  idType: string;
  idNumber: string;
  idFrontKey: string;
  idBackKey: string;
  selfieKey: string;
  corrAddress1: string;
  corrAddress2: string;
  corrCity: string;
  corrPostcode: string;
  corrState: string;
  corrCountry: string;
};

const fieldClass =
  "mt-1 w-full rounded-xl border border-white/10 bg-surface-2 px-3 py-2 text-sm text-white outline-none focus:border-white/20";

export function FlexxiVerify() {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>("Identity");
  const [info, setInfo] = useState<KycState>({
    legalName: "",
    idType: "MyKad / NRIC",
    idNumber: "",
    idFrontKey: "",
    idBackKey: "",
    selfieKey: "",
    corrAddress1: "",
    corrAddress2: "",
    corrCity: "",
    corrPostcode: "",
    corrState: "",
    corrCountry: "MY",
  });
  const [scanning, setScanning] = useState(false);

  const statusQ = useQuery({
    queryKey: ["flexxi-kyc"],
    queryFn: () =>
      api<{
        kycVerified: boolean;
        verifiedViaSeller: boolean;
        kycStatus: keyof typeof FLEXXI_KYC_STATUS_LABELS;
        pendingReview: boolean;
        canSubmit: boolean;
        reviewNote: string | null;
      }>("/flexxi/kyc"),
  });

  const submitMut = useMutation({
    mutationFn: () =>
      api("/flexxi/kyc/submit", {
        method: "POST",
        body: {
          ...info,
          idNumber: info.idNumber.replace(/\s/g, ""),
        },
      }),
    onSuccess: () => {
      toast("success", "Verification submitted for review");
      void qc.invalidateQueries({ queryKey: ["flexxi-kyc"] });
      void qc.invalidateQueries({ queryKey: ["flexxi"] });
    },
    onError: (e: Error) => toast("error", e instanceof ApiError ? e.message : "Submit failed"),
  });

  async function scanId(idFrontKey: string, idBackKey?: string) {
    if (!idFrontKey) return;
    setScanning(true);
    try {
      const res = await api<{ suggestions: Partial<KycState> }>("/flexxi/kyc/scan-id", {
        method: "POST",
        body: { idFrontKey, idBackKey: idBackKey || undefined },
      });
      const s = res.suggestions;
      if (s && Object.keys(s).length > 0) {
        setInfo((prev) => ({
          ...prev,
          legalName: s.legalName ?? prev.legalName,
          idNumber: s.idNumber ?? prev.idNumber,
          corrAddress1: s.corrAddress1 ?? prev.corrAddress1,
          corrAddress2: s.corrAddress2 ?? prev.corrAddress2,
          corrCity: s.corrCity ?? prev.corrCity,
          corrPostcode: s.corrPostcode ?? prev.corrPostcode,
          corrState: s.corrState ?? prev.corrState,
          corrCountry: s.corrCountry ?? prev.corrCountry,
        }));
        toast("success", "Fields updated from your ID — please review before submitting");
      }
    } catch (e) {
      toast("error", e instanceof ApiError ? e.message : "Could not read ID photo");
    } finally {
      setScanning(false);
    }
  }

  const stepIndex = STEPS.indexOf(step);

  function canNext(): boolean {
    if (step === "Identity") {
      return !!info.legalName.trim() && !!info.idNumber.trim() && !!info.idFrontKey && !!info.idBackKey;
    }
    if (step === "Address") {
      return (
        !!info.corrAddress1.trim() &&
        !!info.corrCity.trim() &&
        !!info.corrPostcode.trim() &&
        !!info.corrState.trim()
      );
    }
    return !!info.selfieKey;
  }

  async function next() {
    if (step === "Selfie") {
      submitMut.mutate();
      return;
    }
    if (!canNext()) return;
    setStep(STEPS[stepIndex + 1]!);
  }

  if (statusQ.isLoading) return <PageLoader />;

  const st = statusQ.data;

  if (st?.kycVerified) {
    return (
      <div className="space-y-4 px-4 pt-4 lg:px-6">
        <PageHeader title="FlexxiPlan verification" description="Identity verification for instalments." />
        <div className="glass-panel rounded-2xl p-5 text-sm">
          <p className="font-semibold text-emerald-300">
            {st.verifiedViaSeller ? "Verified via your seller account" : "You are verified for FlexxiPlan"}
          </p>
          <Link to="/inventory" className="mt-3 inline-block text-sm font-semibold text-brand-pink">
            Back to collection
          </Link>
        </div>
      </div>
    );
  }

  if (st?.pendingReview) {
    return (
      <div className="space-y-4 px-4 pt-4 lg:px-6">
        <PageHeader title="FlexxiPlan verification" description="Identity verification for instalments." />
        <div className="glass-panel rounded-2xl p-5 text-sm text-white/70">
          <p className="font-semibold text-amber-300">Under review</p>
          <p className="mt-2 text-white/50">We will notify you when your verification is approved.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-4 pb-8 pt-4 lg:px-6">
      <PageHeader
        title="FlexxiPlan verification"
        description="Upload MyKad and a selfie so staff can approve instalment checkout."
      />

      {st?.reviewNote && (st.kycStatus === "REJECTED" || st.kycStatus === "UPDATE_REQUIRED") ? (
        <div className="rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
          <p className="font-semibold">{FLEXXI_KYC_STATUS_LABELS[st.kycStatus]}</p>
          <p className="mt-1 text-red-100/80">{st.reviewNote}</p>
        </div>
      ) : null}

      <nav className="flex gap-1">
        {STEPS.map((label, i) => (
          <div
            key={label}
            className={`flex-1 rounded-lg px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wide ${
              i === stepIndex
                ? "bg-gradient-to-r from-brand-pink/90 to-brand-blue/80 text-white"
                : i < stepIndex
                  ? "bg-white/10 text-white/70"
                  : "bg-white/5 text-white/40"
            }`}
          >
            {label}
          </div>
        ))}
      </nav>

      <div className="glass-panel space-y-4 rounded-2xl p-5">
        {step === "Identity" && (
          <>
            <label className="block text-[10px] uppercase tracking-wide text-white/40">
              Legal name
              <input
                className={fieldClass}
                value={info.legalName}
                onChange={(e) => setInfo({ ...info, legalName: e.target.value })}
              />
            </label>
            <label className="block text-[10px] uppercase tracking-wide text-white/40">
              ID type
              <select
                className={fieldClass}
                value={info.idType}
                onChange={(e) => setInfo({ ...info, idType: e.target.value, idNumber: "" })}
              >
                {ID_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-[10px] uppercase tracking-wide text-white/40">
              {info.idType === "Passport" ? "Passport number" : "MyKad / NRIC"}
              <input
                className={fieldClass}
                value={info.idNumber}
                maxLength={info.idType === "Passport" ? 20 : IC_MAX}
                onChange={(e) => setInfo({ ...info, idNumber: e.target.value.replace(/\s/g, "") })}
              />
            </label>
            <PrivateKycUploadField
              label="MyKad / NRIC (front)"
              hint="Clear photo of the front of your ID."
              value={info.idFrontKey}
              onChange={(idFrontKey) => {
                setInfo({ ...info, idFrontKey });
                void scanId(idFrontKey, info.idBackKey || undefined);
              }}
              mobileCaptureField="idFront"
            />
            <PrivateKycUploadField
              label="MyKad / NRIC (back)"
              hint="Clear photo of the back of your ID."
              value={info.idBackKey}
              onChange={(idBackKey) => {
                setInfo({ ...info, idBackKey });
                if (info.idFrontKey) void scanId(info.idFrontKey, idBackKey);
              }}
              mobileCaptureField="idBack"
            />
            {scanning ? <p className="text-[11px] text-white/40">Reading ID photo…</p> : null}
          </>
        )}

        {step === "Address" && (
          <>
            <label className="block text-[10px] uppercase tracking-wide text-white/40">
              Address line 1
              <input
                className={fieldClass}
                value={info.corrAddress1}
                onChange={(e) => setInfo({ ...info, corrAddress1: e.target.value })}
              />
            </label>
            <label className="block text-[10px] uppercase tracking-wide text-white/40">
              Address line 2 (optional)
              <input
                className={fieldClass}
                value={info.corrAddress2}
                onChange={(e) => setInfo({ ...info, corrAddress2: e.target.value })}
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-[10px] uppercase tracking-wide text-white/40">
                City
                <input
                  className={fieldClass}
                  value={info.corrCity}
                  onChange={(e) => setInfo({ ...info, corrCity: e.target.value })}
                />
              </label>
              <label className="block text-[10px] uppercase tracking-wide text-white/40">
                Postcode
                <input
                  className={fieldClass}
                  value={info.corrPostcode}
                  onChange={(e) => setInfo({ ...info, corrPostcode: e.target.value })}
                />
              </label>
            </div>
            <label className="block text-[10px] uppercase tracking-wide text-white/40">
              State
              <select
                className={fieldClass}
                value={info.corrState}
                onChange={(e) => setInfo({ ...info, corrState: e.target.value })}
              >
                <option value="">Select state…</option>
                {MY_STATE_LABELS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        {step === "Selfie" && (
          <PrivateKycUploadField
            label="Selfie with MyKad"
            hint="Hold your ID next to your face in good lighting."
            value={info.selfieKey}
            onChange={(selfieKey) => setInfo({ ...info, selfieKey })}
            capture="user"
            mobileCaptureField="selfie"
          />
        )}

        <div className="flex justify-between gap-2 pt-2">
          <Button variant="ghost" disabled={stepIndex === 0} onClick={() => setStep(STEPS[stepIndex - 1]!)}>
            Back
          </Button>
          <Button disabled={!canNext() || submitMut.isPending} onClick={() => void next()}>
            {step === "Selfie" ? (submitMut.isPending ? "Submitting…" : "Submit") : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
}
